<template>
    <div>
        <LoadingSpinner v-if="showLoadingSpinner" />
        <FindMyCar
            v-else-if="showFindMyCar"
            :heading="this.heading"
            :registration="this.registration"
            :mileage="this.mileage.toString()"
        />
        <ConfirmYourCar
            v-else-if="showConfirmYourCar"
        />
        <ServiceOptions v-else-if="showServiceOptions" />
        <SelectADealer
            v-else-if="showSelectADealer"
            v-on:collect-pricing="collectPricing"
            v-on:place-updated="handlePlaceUpdated"
        />
        <SelectDateTime
            v-else-if="showSelectDateTime"
            :submitting="submitting"
            v-on:create-booking="createBooking"
        />
        <YourSummary v-else-if="showYourSummary" />
        <Confirmation v-else-if="showConfirmation" />
    </div>
</template>

<script>
import axios from "axios";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import {
    FindMyCar,
    ConfirmYourCar,
    SelectADealer,
    SelectDateTime,
    YourSummary,
    Confirmation
} from "./components/index";
import ServiceOptions from "./components/Steps/ServiceOptions.vue";
import LoadingSpinner from "./components/LoadingSpinner.vue";
import { customEvent, generateLeadEvent } from '../modules/tracking/AnalyticsEvents';

export default {
    components: {
        LoadingSpinner,
        FindMyCar,
        ConfirmYourCar,
        SelectADealer,
        SelectDateTime,
        ServiceOptions,
        YourSummary,
        Confirmation,
    },
    data() {
        return {
            loading: false,
            submitting: false,
        };
    },
    props: {
        heading: {
            type: String,
            default: 'Book a service online',
            required: false
        },
        vauxhallTakataRecallFix: {
            type: Boolean,
            default: false,
            required: false
        },
        recallOnlyJourney: {
            type: Boolean,
            default: false,
            required: false
        }
    },
    computed: {
        ...mapState("Dealers", ["dealers", "selectedDealer"]),
        ...mapState("OSB", [
            "step",
            "registration",
            "mileage",
            "shortOnlineServiceBooking",
            "shortOnlineServiceBooking",
            "selectedOptions",
            "faultDescription",
            "pricingData",
            "selectedVehicle",
            "otherSelected",
            "nonFranchiseVehicle",
            "carNotFound",
            "brand",
            "customerAddress",
            "bookingId",
            "loginUrl"
        ]),
        ...mapGetters("OSB", ["bookingDate", "vehicleDetailsFormatted", "selectedOptionsArray", "subTotal"]),

        queryParams() {
            return new URLSearchParams(window.location.search);
        },
        showLoadingSpinner() {
            return this.loading && this.shortOnlineServiceBooking;
        },
        showFindMyCar() {
            return this.step === 1 && !this.shortOnlineServiceBooking;
        },
        showConfirmYourCar() {
            return this.step === 2 && !this.shortOnlineServiceBooking;
        },
        showSelectADealer() {
            return this.step === 3;
        },
        showServiceOptions() {
            return this.step === (this.shortOnlineServiceBooking ? 4 : 4);
        },
        showSelectDateTime() {
            return this.step === (this.shortOnlineServiceBooking ? 5 : 5);
        },
        showAdditionalServices() {
            return this.step === 6 && !this.shortOnlineServiceBooking;
        },
        showYourSummary() {
            return this.step === 6 && !this.shortOnlineServiceBooking;
        },
        showConfirmation() {
            return this.step === (this.shortOnlineServiceBooking ? 6 : 7);
        },
    },
    methods: {
        ...mapActions("Dealers", ["initializeLocation"]),
        ...mapActions("OSB", [
            "collectPricing",
            "lookupVehicle",
            "selectBookingDealer",
        ]),

        ...mapMutations("Auth", ['setHasAccount', 'setVehicleAdded', 'setAuthenticated']),
        ...mapMutations("OSB", [
            "enableShortOnlineServiceBooking",
            "incrementStep",
            "setBookingId",
            "setBrand",
            "setCarNotFound",
            "setCustomerAddress",
            "setIsNonFranchiseVehicle",
            "setMileage",
            "setOtherSelected",
            "setRegistration",
            "setSelectedVehicle",
            "setStep",
            "setRecallOnlyJourney",
            "setVauxhallTakataRecallFix",
        ]),

        collectUserVehicleData() {
            const userVehicleId = this.queryParams.get("user_vehicle_id");

            if (!userVehicleId) {
                return;
            }

            this.loading = true;

            axios.get(`/api/osb/user-vehicle/${userVehicleId}`)
                .then((response) => {
                    this.setRegistration(response.data.reg_no);
                    this.setMileage(response.data.mileage);

                    this.lookupVehicle()
                        .then(response => {
                            this.incrementStep();
                            this.loading = false;
                        })
                        .catch(error => {
                            console.error('Error during vehicle lookup:', error);
                            this.loading = false;
                        });
                    this.setSelectedVehicle(response.data.vehicle)
                })
                .catch((error) => {
                    console.error(error);
                });
        },

        collectLocationData() {
            if (! this.shortOnlineServiceBooking) {
                return;
            }

            const params = new URLSearchParams(window.location.search);
            const locationId = params.get("selected_dealer");
            const dealer = this.dealers.find((dealer) => { return dealer.id == locationId });

            if (dealer) {
                this.selectBookingDealer(dealer);
                this.setStep(3);
            }
        },

        checkIfAuthenticated() {
            return new Promise((resolve, reject) => {
                this.$store.dispatch('Auth/checkAuthenticated')
                    .then(() => {
                        resolve();
                    })
                    .catch(error => {
                        console.error('Authentication error:', error);
                        reject(error);
                    });
            });
        },

        createBooking() {
            const customerData = this.$store.state.CustomerData;
            const formData = new FormData();

            formData.set("title", customerData.title);
            formData.set("first-name", customerData.firstName);
            formData.set("last-name", customerData.lastName);
            formData.set("email", customerData.email);
            formData.set("telephone", customerData.telephone);
            formData.set("location-id", this.selectedDealer.id);
            formData.set("vehicle", JSON.stringify(this.vehicleDetailsFormatted));

            formData.set("booking-id", this.bookingId);
            formData.set("brand", this.brand !== "" ? this.brand : window.brand);
            formData.set("booking-date", this.bookingDate);
            const latestSelectableTime = this.$store.getters['OSB/latestSelectableTime'];
            if (latestSelectableTime !== null) {
                formData.set('latest_selectable_time', latestSelectableTime);
            }
            formData.set("selected-options", JSON.stringify(this.selectedOptionsArray));
            formData.set("system-diagnostic-selected", this.selectedOptions.system_diagnostic ? "1" : "0");
            formData.set("fault-description", this.selectedOptions.system_diagnostic ? this.faultDescription : "");
            formData.set("manufacturer-updates-selected", JSON.stringify(this.$store.getters['OSB/manufacturerUpdatesSelected']));
            formData.set("service-name", this.pricingData.service_pricing.name);
            formData.set("sub-total", this.subTotal);
            formData.set("location", this.customerAddress || "");

            this.submitting = true;
            axios
                .post("/api/osb/submit", formData)
                .then((response) => {
                    customEvent('OSB', 'Click', 'booking confirmed');
                    generateLeadEvent('osb_booking');
                    this.setVehicleAdded(response.data.vehicle_added)
                    this.setHasAccount(response.data.has_account)

                    this.submitting = false;

                    this.incrementStep();
                    this.scrollToTop();
                })
                .catch((error) => {
                    console.error(error);
                    this.submitting = false;
                    this.$store.dispatch('OSB/handleBookingUnavailable', error);
                });
        },

        handlePlaceUpdated(place) {
            if (typeof place.formatted_address != 'undefined') {
                this.setCustomerAddress(place.formatted_address)
            }
        },

        scrollToTop() {
            window.scrollTo(0, 0);
        },
    },
    watch: {
        step() {
            this.scrollToTop();
        }
    },
    created() {
        this.setRecallOnlyJourney(this.recallOnlyJourney);
        this.setVauxhallTakataRecallFix(this.vauxhallTakataRecallFix);

        this.setBrand(window.brand);

        if (this.queryParams.get('short_booking')) {
            this.enableShortOnlineServiceBooking();
        }

        this.initializeLocation({
            getAddressInfo: true,
            getPhoneInfo: true,
        }).then(() => {
            this.collectLocationData();
        });
    },
    mounted() {
        this.checkIfAuthenticated()

        if (this.shortOnlineServiceBooking) {
            this.collectUserVehicleData();
        }
    }
};
</script>
